import { createSelector } from "reselect";

import { getPeriodFromPreset } from "src/components/TimeFilter";
import type { TimeOption } from "src/components/TimeFilter";
import {
  dashboardDateRangeSelector,
  transactionDateRangeSelector,
  effectiveUserTimeZoneSelector,
} from "src/store/system/selector";
import { oldestSaleYearSelector } from "src/store/sale/selector";
import { oldestTransactionYearSelector } from "src/store/transaction/selector";
import { getDateTimePartsInTimeZone } from "src/utils/timezone";

import type { AppState } from "..";

const getUserDataLoadState = (state: AppState) => state.userDataLoad;

function timeOptionStart(option: TimeOption, timeZone: string): Date {
  if (option.type === "custom") return option.period.start;
  return getPeriodFromPreset({ type: option.type }, timeZone).start;
}

/** Earliest start among dashboard and transaction date filters (resolved). */
export const calculatedLoadedFromDateSelector = createSelector(
  [
    dashboardDateRangeSelector,
    transactionDateRangeSelector,
    effectiveUserTimeZoneSelector,
  ],
  (dashboardResolved, transactionResolved, timeZone) => {
    const d0 = timeOptionStart(dashboardResolved.timeOption, timeZone);
    const t0 = timeOptionStart(transactionResolved.timeOption, timeZone);
    return d0.getTime() <= t0.getTime() ? d0 : t0;
  }
);

export const salesTransactionsFirestorePendingSelector = createSelector(
  [getUserDataLoadState],
  (s) => s.salesTransactionsFirestoreLoading
);

export const initialSalesTransactionsSyncCompleteSelector = createSelector(
  [getUserDataLoadState],
  (s) => s.initialSalesTransactionsSyncComplete
);

export const oldestReportDataYearSelector = createSelector(
  [
    oldestSaleYearSelector,
    oldestTransactionYearSelector,
    effectiveUserTimeZoneSelector,
  ],
  (oldestSaleYear, oldestTransactionYear, timeZone) => {
    const nowYear = getDateTimePartsInTimeZone(new Date(), timeZone).year;
    const years = [oldestSaleYear, oldestTransactionYear].filter(
      (year): year is number => typeof year === "number" && !isNaN(year)
    );
    if (!years.length) return nowYear;
    return Math.min(...years);
  }
);

export const reportAvailableYearsSelector = createSelector(
  [oldestReportDataYearSelector, effectiveUserTimeZoneSelector],
  (oldestYear, timeZone) => {
    const nowYear = getDateTimePartsInTimeZone(new Date(), timeZone).year;
    const start = Math.min(oldestYear, nowYear);
    const years: number[] = [];
    for (let year = nowYear; year >= start; year -= 1) {
      years.push(year);
    }
    return years;
  }
);
